import { type ReactNode } from 'react';
import { ArrowLeftRight, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { MonacoPane } from './MonacoPane';

/**
 * Standard "input ↔ output + reverse + options" layout for codec-style
 * tools. Owns no business state — the parent decides what `inputValue`
 * and `outputValue` mean. The reverse button just calls `onSwap`; the
 * parent typically pivots input ↔ output and toggles direction.
 *
 * Slots:
 *   - `optionsSlot`  → top toolbar, right-aligned (e.g. variant select)
 *   - `extraSlot`    → below the two panes (e.g. URL Query Inspector)
 */
export interface CodecPanelProps {
  inputLabel: string;
  outputLabel: string;
  inputValue: string;
  onInputChange: (next: string) => void;
  outputValue: string;
  outputError?: string;
  onSwap?: () => void;
  inputLanguage?: string;
  outputLanguage?: string;
  inputPlaceholder?: string;
  optionsSlot?: ReactNode;
  extraSlot?: ReactNode;
  /**
   * Show the byte-length counter under the output. Codec-style tools
   * (base64/hex) lean on this for sanity-checking; URL doesn't.
   */
  showOutputLength?: boolean;
  className?: string;
}

export function CodecPanel(props: CodecPanelProps): JSX.Element {
  const {
    inputLabel,
    outputLabel,
    inputValue,
    onInputChange,
    outputValue,
    outputError,
    onSwap,
    inputLanguage = 'plaintext',
    outputLanguage = 'plaintext',
    inputPlaceholder,
    optionsSlot,
    extraSlot,
    showOutputLength = true,
    className,
  } = props;

  const swapDisabled = outputError !== undefined || outputValue.length === 0;

  return (
    <section className={clsx('flex flex-col gap-3', className)}>
      <div className="card-pad flex flex-wrap items-center gap-3">
        {onSwap && (
          <button
            type="button"
            onClick={onSwap}
            className="btn-ghost text-xs disabled:cursor-not-allowed disabled:opacity-40"
            title="把当前输出回灌为输入并反转方向"
            disabled={swapDisabled}
          >
            <ArrowLeftRight className="h-3.5 w-3.5" />
            反转
          </button>
        )}
        {optionsSlot && <div className="ml-auto flex flex-wrap items-center gap-3">{optionsSlot}</div>}
      </div>

      <div className="grid min-h-[260px] flex-1 grid-cols-1 gap-3 lg:grid-cols-2">
        <MonacoPane
          title={inputLabel}
          value={inputValue}
          onChange={onInputChange}
          language={inputLanguage}
          placeholder={inputPlaceholder}
          actions={['clear']}
        />
        <MonacoPane
          title={outputLabel}
          value={outputError ? '' : outputValue}
          language={outputLanguage}
          readOnly
          actions={['copy', 'fold-all', 'unfold-all']}
          statusRight={
            outputError ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-rose-600 dark:text-rose-400">
                <XCircle className="h-3 w-3" />
                解析失败
              </span>
            ) : showOutputLength && outputValue.length > 0 ? (
              <span className="text-[11px] text-ink-dim">长度 {outputValue.length}</span>
            ) : undefined
          }
        />
      </div>

      {outputError && (
        <p className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-[12px] text-rose-600 dark:text-rose-400">
          <XCircle className="h-3.5 w-3.5" />
          {outputError}
        </p>
      )}

      {extraSlot}
    </section>
  );
}
